"use client";
/**
 * Opener sound channel: the reel tick and the rarity-dependent reveal, fired as the shared sound
 * events (`opener.spinner.tick`, `opener.reveal.bad|rare|mega`, `battle.*` in a battle) through
 * the page's one sound engine (`./openerSoundEngine`). The rarity → reveal mapping is ported from
 * boxed-frontend's `revealOutcome` (its signup-box special case dropped; battles' invert is the
 * caller's).
 */
import type { SoundManifest } from "@loot-labs/sound";
import { SOUND_GAIN_DB_MAX, SOUND_GAIN_DB_MIN } from "@loot-os/api-contract";
import { useEffect, useMemo, useRef } from "react";
import {
  addHostSoundLayer,
  addShopperSoundLevel,
  type OpenerSoundEvent,
  openerSoundEngine,
  type ShopperSoundLevelHandle,
  type ShopperSoundLevelInput,
  type ShopperSoundLevelSource,
  shopperSoundLevel,
  subscribeShopperSoundLevel,
} from "./openerSoundEngine";

export type { OpenerSoundEvent };

/** A host's own sound files, one URL per opener sound. */
export interface OpenerSoundUrls {
  tick?: string;
  badPrize?: string;
  rarePrize?: string;
  megaPrize?: string;
}

/**
 * A store's sounds keyed by event: a URL, a URL with a trim in dB (-24 to 6, 0 when absent), or
 * `{ off: true }`, which plays nothing. Sits above `sounds` / `soundBaseUrl`. An event left out
 * plays what those give it, and a battle event with no sound of its own plays its opener twin's;
 * one turned off stays silent.
 */
export type OpenerSoundEventUrls = Partial<
  Record<OpenerSoundEvent, string | { url: string; gainDb?: number } | { off: true }>
>;

/**
 * `false` disables all sounds; an object overrides individual URLs;
 * `undefined` falls back to `soundBaseUrl`-derived defaults (silent when no
 * base URL is configured — the hosted CDN default gets wired later).
 */
export type SoundsProp = false | OpenerSoundUrls | undefined;

/** Which events the channel fires: a battle's fall back to the opener's when nothing maps them. */
export type OpenerSoundScope = "opener" | "battle";

const URL_FILENAMES: Required<OpenerSoundUrls> = {
  tick: "tick.mp3",
  badPrize: "bad-prize.mp3",
  rarePrize: "rare-prize.mp3",
  megaPrize: "mega-prize.mp3",
};

const URL_EVENTS: Record<keyof OpenerSoundUrls, OpenerSoundEvent> = {
  tick: "opener.spinner.tick",
  badPrize: "opener.reveal.bad",
  rarePrize: "opener.reveal.rare",
  megaPrize: "opener.reveal.mega",
};

/** Resolves the effective files; null = sounds disabled/unconfigured. */
export function resolveOpenerSoundUrls(
  sounds: SoundsProp,
  soundBaseUrl?: string,
): OpenerSoundUrls | null {
  if (sounds === false) return null;
  const base = soundBaseUrl?.replace(/\/$/, "");
  const defaults: OpenerSoundUrls = base
    ? {
        tick: `${base}/${URL_FILENAMES.tick}`,
        badPrize: `${base}/${URL_FILENAMES.badPrize}`,
        rarePrize: `${base}/${URL_FILENAMES.rarePrize}`,
        megaPrize: `${base}/${URL_FILENAMES.megaPrize}`,
      }
    : {};
  const urls = { ...defaults, ...sounds };
  return Object.values(urls).some(Boolean) ? urls : null;
}

const MIME_BY_EXTENSION: Record<string, string> = {
  mp3: "audio/mpeg",
  m4a: "audio/mp4",
  mp4: "audio/mp4",
  aac: "audio/aac",
  webm: "audio/webm",
  ogg: "audio/ogg",
  opus: "audio/ogg",
  wav: "audio/wav",
};

function mimeFor(url: string): string {
  const extension = /\.([a-z0-9]+)(?:[?#]|$)/i.exec(url)?.[1]?.toLowerCase() ?? "";
  return MIME_BY_EXTENSION[extension] ?? "";
}

/** The host's files as a sound theme: one asset and one opener event per file. */
export function openerSoundTheme(urls: OpenerSoundUrls): SoundManifest {
  const theme: SoundManifest = { schema: 1, assets: {}, events: {} };
  for (const [key, event] of Object.entries(URL_EVENTS) as [keyof OpenerSoundUrls, string][]) {
    const url = urls[key];
    if (!url) continue;
    theme.assets[key] = { sources: [{ url, type: mimeFor(url) }] };
    theme.events[event] = { variations: [{ asset: key }] };
  }
  return theme;
}

/**
 * The store's event-keyed sounds as a sound theme: one asset and one mapping per event. An event
 * turned off maps to no variations, the engine's deliberate silence, so nothing beneath it or
 * along its fallback plays.
 */
export function openerEventSoundTheme(events: OpenerSoundEventUrls): SoundManifest {
  const theme: SoundManifest = { schema: 1, assets: {}, events: {} };
  for (const [event, setting] of Object.entries(events)) {
    if (typeof setting === "object" && "off" in setting) {
      if (setting.off === true) theme.events[event] = { variations: [] };
      continue;
    }
    const url = typeof setting === "string" ? setting : setting?.url;
    if (!url) continue;
    const gainDb = typeof setting === "object" ? setting.gainDb : undefined;
    theme.assets[event] = { sources: [{ url, type: mimeFor(url) }] };
    theme.events[event] = {
      variations: [{ asset: event }],
      ...(typeof gainDb === "number" && Number.isFinite(gainDb)
        ? { gainDb: Math.min(SOUND_GAIN_DB_MAX, Math.max(SOUND_GAIN_DB_MIN, gainDb)) }
        : {}),
    };
  }
  return theme;
}

/**
 * Applies the shopper's mute and volume (0 to 100) to the page's sound engine while mounted.
 * `null` applies nothing. Pass the same values to every render that should not re-apply.
 */
export function useShopperSoundLevel(
  input: ShopperSoundLevelInput | null,
  source: ShopperSoundLevelSource,
): void {
  const handleRef = useRef<ShopperSoundLevelHandle | null>(null);
  const active = input !== null;
  const muted = input?.muted;
  const volume = input?.volume;
  // The slot lives as long as the component, so a change updates it in place.
  useEffect(() => {
    const handle = addShopperSoundLevel(source);
    handleRef.current = handle;
    return () => {
      handle.remove();
      handleRef.current = null;
    };
  }, [source]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: `source` re-creates the slot above, which must be filled again
  useEffect(() => {
    handleRef.current?.set(active ? { muted, volume } : null);
  }, [active, muted, volume, source]);
}

/** Ported mapping: 2/3 → rare, 4+ → mega, everything else → bad. */
function revealEvent(scope: OpenerSoundScope, rarity: number): OpenerSoundEvent {
  if (rarity === 2 || rarity === 3) return `${scope}.reveal.rare`;
  return rarity >= 4 ? `${scope}.reveal.mega` : `${scope}.reveal.bad`;
}

export interface OpenerSoundChannel {
  /** Wheel tick as items pass the pointer (called from motion onUpdate). */
  playTickSound: () => void;
  /** Reveal sound for a settled card of the given rarity. */
  playRaritySound: (rarity: number) => void;
  /** Resume the AudioContext from a user gesture (the Open click), and load the sounds. */
  unlock: () => void;
  /**
   * False only when the host turned the opener's sounds off (`sounds={false}`), whether or not it
   * configured sound files. An opener that synthesizes its own sound (Neon Slam, Retail Box) follows it.
   */
  readonly enabled: boolean;
  /** At least one sound is set: `soundEvents`, or files from `sounds` / `soundBaseUrl`. */
  readonly configured: boolean;
  /** The shopper muted sounds (or set the volume to 0). Synthesized sound follows it too. */
  readonly muted: boolean;
  /** The shopper's volume as an engine gain, 0 to 1 (a square curve of the 0 to 100 slider). */
  readonly gain: number;
  /** Calls `listener` when `muted` or `gain` changes. The channel itself never changes. */
  subscribe: (listener: () => void) => () => void;
}

/** Whether any event has a file to play, rather than only events turned off. */
function hasPlayableEvent(events: OpenerSoundEventUrls | undefined): boolean {
  return Object.values(events ?? {}).some(
    (setting) => typeof setting === "string" || (setting !== undefined && "url" in setting),
  );
}

export function useOpenerSounds({
  sounds,
  soundBaseUrl,
  soundEvents,
  scope = "opener",
}: {
  sounds: SoundsProp;
  soundBaseUrl?: string;
  soundEvents?: OpenerSoundEventUrls;
  scope?: OpenerSoundScope;
}): OpenerSoundChannel {
  const urls = resolveOpenerSoundUrls(sounds, soundBaseUrl);
  // Keyed by content, so a host passing a fresh object each render doesn't re-layer the engine.
  const urlsKey = urls ? JSON.stringify(urls) : null;
  const eventsKey =
    sounds !== false && soundEvents && Object.keys(soundEvents).length > 0
      ? JSON.stringify(soundEvents)
      : null;
  // A store whose every event is turned off has nothing to mute: it is not configured.
  const playable = urls !== null || (eventsKey !== null && hasPlayableEvent(soundEvents));
  const configuredRef = useRef(playable);
  configuredRef.current = playable;
  // Off only when the host said so: a host with no sound files still wants synthesized sound.
  const offRef = useRef(sounds === false);
  offRef.current = sounds === false;
  const scopeRef = useRef(scope);
  scopeRef.current = scope;

  // One effect for both layers, so a change to either re-adds them in order, events on top.
  // Arms the first-gesture unlock: a page that goes straight to an opener hears it on first tap.
  const configured = playable;
  useEffect(() => {
    if (configured) openerSoundEngine();
  }, [configured]);

  useEffect(() => {
    const removers: (() => void)[] = [];
    if (urlsKey) {
      removers.push(addHostSoundLayer(openerSoundTheme(JSON.parse(urlsKey) as OpenerSoundUrls)));
    }
    if (eventsKey) {
      removers.push(
        addHostSoundLayer(openerEventSoundTheme(JSON.parse(eventsKey) as OpenerSoundEventUrls)),
      );
    }
    return () => {
      for (const remove of removers.reverse()) remove();
    };
  }, [urlsKey, eventsKey]);

  return useMemo<OpenerSoundChannel>(
    () => ({
      get enabled() {
        return !offRef.current;
      },
      get configured() {
        return configuredRef.current;
      },
      get muted() {
        return shopperSoundLevel().muted;
      },
      get gain() {
        return shopperSoundLevel().gain;
      },
      subscribe: subscribeShopperSoundLevel,
      playTickSound: () => {
        if (configuredRef.current) openerSoundEngine().play(`${scopeRef.current}.spinner.tick`);
      },
      playRaritySound: (rarity: number) => {
        if (configuredRef.current) openerSoundEngine().play(revealEvent(scopeRef.current, rarity));
      },
      unlock: () => {
        if (!configuredRef.current) return;
        const engine = openerSoundEngine();
        engine.unlock();
        const current = scopeRef.current;
        engine.preload([
          `${current}.spinner.tick`,
          `${current}.reveal.bad`,
          `${current}.reveal.rare`,
          `${current}.reveal.mega`,
        ]);
      },
    }),
    [],
  );
}
